import { Flex, Form, FormInstance, Input, TimePicker } from 'antd'
import { FormattedMessage, useIntl } from 'react-intl'
import cn from 'classnames'
import Text from 'shared/components/typography/Text'
import Collapse from 'shared/components/Collapse'
import { lang } from '../lang'
import s from '../s.module.css'
import { MediaPriceLimit } from '../../hooks/useMediaPricesLimits'
import { lang as commonLang } from '../../lang'
import CollapseHeader from '../CollapseHeader'
import VideoIcon from './video.svg?react'
import { formKeys } from '@/pages/Donation/components/DonationForm/constants'
import { ICurrency } from '@/pages/Donation/api/types'
import {
  getTimePickerValueFromSeconds,
  getTimestampFromYoutubeLink,
  removeTimestampFromYoutubeLink,
} from '@/pages/Donation/utils'
import { CustomizeType, FormValues } from '@/pages/Donation/types'
import Label from '@/shared/components/Label'
import PayToUploadButton from '@/pages/Donation/components/PayToUploadButton'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext'

type Props = {
  baseCurrency: ICurrency | null
  mediaPriceLimit?: MediaPriceLimit
  isSelected: boolean
  onChangeCustomizeType: (type: CustomizeType[number]) => void
  handleClose: (type: CustomizeType[number]) => void
  onAddAmount: (type: CustomizeType[number]) => void
  form: FormInstance<FormValues>
}

export default function Video({
  baseCurrency,
  mediaPriceLimit,
  onAddAmount,
  isSelected,
  onChangeCustomizeType,
  handleClose,
  form,
}: Props) {
  const intl = useIntl()
  const { config } = useStreamerConfigContext()
  const maxDurationInSeconds = config?.video_donation_config?.max_duration_seconds
  const maxDurationInMinutes = maxDurationInSeconds && Math.floor(maxDurationInSeconds / 60)

  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const seconds = getTimestampFromYoutubeLink(e.target.value)

    if (seconds) {
      const timestamp = getTimePickerValueFromSeconds(seconds)
      form.setFieldValue(formKeys.youtubeStart, timestamp)
    }

    const link = removeTimestampFromYoutubeLink(e.target.value)
    form.setFieldValue(formKeys.youtubeLink, link)
  }

  const isClickable = !isSelected && mediaPriceLimit == null

  const handleClick = () => {
    if (isClickable) {
      onChangeCustomizeType('video')
    }
  }

  return (
    <Flex
      className={cn(s.wrap, {
        [s.clickable]: isClickable,
      })}
      vertical
      onClick={handleClick}
      gap={16}
    >
      <CollapseHeader
        icon={<VideoIcon />}
        title={intl.formatMessage(lang.addVideo)}
        shouldShowCloseButton={isSelected}
        shouldShowLimit={mediaPriceLimit != null}
        onClose={() => handleClose('video')}
        limit={
          <Text>
            <FormattedMessage
              {...commonLang.payToAdd}
              values={{
                type: intl.formatMessage(lang.video),
                action: (
                  <PayToUploadButton
                    amount={Number(mediaPriceLimit?.payAmount)}
                    currencyCode={baseCurrency?.code}
                    onClick={() => onAddAmount('video')}
                  />
                ),
              }}
            />
          </Text>
        }
      />
      <Collapse isOpen={isSelected}>
        <Flex vertical gap={4}>
          <Flex gap={16}>
            <Form.Item
              name={formKeys.youtubeLink}
              label={
                <Label>
                  <FormattedMessage {...lang.youtubeLink} />
                </Label>
              }
              className={s.youtubeLinkFormItem}
            >
              <Input placeholder={intl.formatMessage(lang.youtubePlaceholder)} onChange={handleLinkChange} />
            </Form.Item>
            <Form.Item
              name={formKeys.youtubeStart}
              label={
                <Label>
                  <FormattedMessage {...lang.youtubeStart} />
                </Label>
              }
              style={{ flexBasis: '106px' }}
            >
              <TimePicker needConfirm={false} placeholder="00:00:00" showNow={false} format="HH:mm:ss" />
            </Form.Item>
          </Flex>
          {maxDurationInMinutes != null && (
            <Text fontSize={14} fontType="body" type="tertiary">
              <FormattedMessage {...lang.videoDurationDisclaimer} values={{ duration: maxDurationInMinutes }} />
            </Text>
          )}
        </Flex>
      </Collapse>
    </Flex>
  )
}
